import type { ReactNode } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * COMPATIBILITY SHIM — legacy Modal API over shadcn's Dialog.
 *
 * Notable behaviour change: the hand-rolled body-overflow lock is gone. Radix
 * owns scroll locking, and running both meant two components restoring
 * document.body.style.overflow independently — nesting or fast open/close could
 * leave the page unscrollable. Escape and focus handling are Radix's too.
 */

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Max width in px. Default 560. */
  maxWidth?: number;
  /** Set false to disable Escape/backdrop close for critical confirmations. */
  dismissible?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  maxWidth = 560,
  dismissible = true,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        showCloseButton={dismissible}
        className="flex max-h-[calc(100dvh-32px)] flex-col gap-0 p-0 sm:max-w-(--modal-max-width)"
        style={{ '--modal-max-width': `${maxWidth}px` } as React.CSSProperties}
        onInteractOutside={(e) => { if (!dismissible) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!dismissible) e.preventDefault(); }}
      >
        {/* DialogTitle is required for accessibility even when there is no
            visible heading — Radix warns and screen readers get no label. */}
        <DialogHeader className={title ? 'px-6 pt-5' : 'sr-only'}>
          <DialogTitle className={title ? 'font-heading text-xl font-bold' : undefined}>
            {title ?? 'Dialog'}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
        {footer && (
          <DialogFooter className="border-t px-6 pt-4 pb-6">{footer}</DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
